/**
 * Firebase Configuration for MarIA Health Care
 *
 * Uses Firebase Authentication for GDPR-compliant user identification.
 * Supports both Anonymous and Google Sign-In authentication.
 *
 * UPDATE (2026-01-13): Added Google Sign-In support using modular SDK.
 * Following official Firebase pattern from:
 * https://firebase.google.com/docs/auth/web/google-signin
 * https://github.com/firebase/quickstart-js/blob/master/auth/google-popup.ts
 *
 * @see Issue #177 - Frontend Firebase Auth Integration
 */
import { initializeApp, FirebaseApp } from 'firebase/app';
import {
    getAuth,
    Auth,
    signInAnonymously,
    signInWithPopup,
    GoogleAuthProvider,
    onAuthStateChanged,
    User,
    UserCredential
} from 'firebase/auth';

// Firebase configuration from environment variables
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Validate required config
const requiredKeys = ['apiKey', 'authDomain', 'projectId'] as const;
for (const key of requiredKeys) {
    if (!firebaseConfig[key]) {
        console.error(`Missing required Firebase config: VITE_FIREBASE_${key.toUpperCase()}`);
    }
}

// Initialize Firebase
let app: FirebaseApp;
let auth: Auth;

try {
    app = initializeApp(firebaseConfig);
    auth = getAuth(app);
} catch (error) {
    console.error('Failed to initialize Firebase:', error);
    throw error;
}

// Google Auth Provider - following Firebase quickstart pattern
const googleProvider = new GoogleAuthProvider();

/**
 * Sign in anonymously to Firebase.
 * @returns Promise<UserCredential> with the anonymous user
 */
export async function signInAnonymouslyToFirebase(): Promise<UserCredential> {
    return signInAnonymously(auth);
}

/**
 * Sign in with Google using popup.
 * Following official Firebase pattern from google-popup.ts quickstart.
 * @returns Promise<UserCredential> with the Google user
 */
export async function signInWithGooglePopup(): Promise<UserCredential> {
    return signInWithPopup(auth, googleProvider);
}

/**
 * Get the current user's ID token for backend authentication.
 * @param forceRefresh - If true, forces token refresh even if not expired
 * @returns Promise<string> The Firebase ID token
 * @throws Error if no user is signed in
 */
export async function getIdToken(forceRefresh: boolean = false): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
        throw new Error('No user signed in');
    }
    return user.getIdToken(forceRefresh);
}

/**
 * Check if the current user is anonymous.
 */
export function isAnonymousUser(): boolean {
    return auth.currentUser?.isAnonymous ?? false;
}

/**
 * Sign out the current user.
 */
export async function signOutFirebase(): Promise<void> {
    return auth.signOut();
}

export { app, auth, onAuthStateChanged, googleProvider };
export type { User, UserCredential };
